import { formatDistanceToNow } from 'date-fns';
import type { Plan } from '@/hooks/useUserPlans';
import type { ButtonStyleType } from '@simplywallst/ui-core';
import { useTranslation } from '@/hooks/useTranslation';
import { useUtmExperiment } from '@/hooks/usePlanUtmExperiment';
import { useCallback } from 'react';
export const defaultPlan: Plan = {
  id: '',
  name: '',
  amount: 0,
  interval: 'year',
  currencyISO: '',
  canTrial: false,
  type: 'free',
  visible: false,
  uacCompaniesAnalysis: 5,
  features: {
    institutionalQualityData: false,
    globalMarkets: false,
    companyReportsPerMonth: 0,
    companiesAnalysedInPortfolio: 0,
    watchlist: 0,
    totalPortfolios: 0,
    stockScreener: false,
    exportToPdf: false,
    dataAudit: false,
    stockIdeaViews: false,
  },
};

export interface Feature {
  code: string;
  text: string;
}

export enum PlanCTA {
  SignUp = 'Sign up',
  Subscribe = 'Subscribe',
  Current = 'Current',
  Downgrade = 'Downgrade',
  Upgrade = 'Upgrade',
  Trial = 'Trial', //Show customer trial countdown
  Continue = 'Continue', //Ask customer to continue with free plan when trial ends
  Resume = 'Resume My Subscription', //Ask customer to resume their subscription if they have cancelled
  Disabled = 'Disabled', //When the other plan was cancelled this one remains disabled
}

export enum UserStatus {
  guest = 'guest',
  free = 'free',
  trial = 'trial',
  trialEnded = 'trialEnded',
  canceled = 'canceled',
  premiumMonthly = 'premiumMonth',
  premiumYearly = 'premiumYear',
  unlimitedMonthly = 'unlimitedMonth',
  unlimitedYearly = 'unlimitedYear',
}

export const defaultPlansPrice = {
  [UserStatus.premiumMonthly]: 0,
  [UserStatus.premiumYearly]: 0,
  [UserStatus.unlimitedMonthly]: 0,
  [UserStatus.unlimitedYearly]: 0,
};
export const userStatusData = {
  premium: {
    year: {
      status: UserStatus.premiumYearly,
    },
    month: {
      status: UserStatus.premiumMonthly,
    },
  },
  unlimited: {
    year: {
      status: UserStatus.unlimitedYearly,
    },
    month: {
      status: UserStatus.unlimitedMonthly,
    },
  },
};

export type BillingPeriod = 'year' | 'month';

type ButtonState = {
  cta: string;
  btnStyle: ButtonStyleType;
  action:
    | 'disabled'
    | 'renew'
    | 'upgrade'
    | 'resume'
    | 'continue'
    | 'login'
    | 'downgrade'
    | 'cancelcancellation';
};

export const DefaultButtonState: ButtonState = {
  cta: 'Get Started',
  btnStyle: 'gentleLargeReverse',
  action: 'login',
};

type PlanButtonState = {
  free: Record<string, ButtonState>;
  premium: Record<
    string,
    ButtonState | { month: ButtonState; year: ButtonState }
  >;
  unlimited: Record<
    string,
    ButtonState | { month: ButtonState; year: ButtonState }
  >;
};

export function usePlanButtonState() {
  const t = useTranslation();
  const { isUtmExperiment } = useUtmExperiment();
  const getButtonState = useCallback(
    (hasCanceled: boolean, cancelledAt: number, currentPlan: string) => {
      const daysUntilCancel =
        cancelledAt !== 0 && cancelledAt !== null
          ? formatDistanceToNow(cancelledAt)
          : '';
      const cacncelledPlanButtonState: ButtonState = {
        cta: t('plan.cta.free.cancelled', {
          plan: currentPlan,
          days: daysUntilCancel,
        }),
        btnStyle: 'gentleLargeReverse',
        action: 'disabled',
      };
      const downgradeButtonState: ButtonState = {
        cta: t('plan.cta.free.downgrade'),
        btnStyle: 'loudLarge',
        action: hasCanceled ? 'renew' : 'downgrade',
      };

      const choosePremiumButtonState: ButtonState = {
        cta: t('plan.cta.choosePlan', {
          plan: 'Premium',
        }),
        btnStyle: 'louderLarge',
        action: hasCanceled ? 'renew' : 'upgrade',
      };

      const switchToAnnualButtonState: ButtonState = {
        cta: t('plan.cta.switchToAnnual'),
        btnStyle: 'louderLarge',
        action: hasCanceled ? 'renew' : 'upgrade',
      };

      const switchToMonthlyButtonState: ButtonState = {
        cta: t('plan.cta.switchToMonthly'),
        btnStyle: 'gentleLargeReverse',
        action: hasCanceled ? 'renew' : 'upgrade',
      };

      const chooseUnlimitedButtonState: ButtonState = {
        cta: t('plan.cta.choosePlan', {
          plan: 'Unlimited',
        }),
        btnStyle: 'louderLarge',
        action: hasCanceled ? 'renew' : 'upgrade',
      };

      const resumeSubscriptionButtonState: ButtonState = {
        cta: t('plan.cta.resumePlan'),
        btnStyle: 'louderLarge',
        action: 'cancelcancellation',
      };

      const currentPlanButtonState: ButtonState = {
        cta: t('plan.cta.currentPlan'),
        btnStyle: 'gentleLargeReverse',
        action: 'disabled',
      };

      return {
        free: {
          [UserStatus.guest]: {
            cta: isUtmExperiment
              ? t('plan.cta.currentPlan')
              : t('plan.cta.free.guest'),
            btnStyle: 'gentleLargeReverse',
            action: isUtmExperiment ? 'disabled' : 'login',
          },
          [UserStatus.free]: currentPlanButtonState,
          [UserStatus.trial]: {
            cta: t('plan.cta.free.trial', {
              plan: currentPlan,
            }),
            btnStyle: 'gentleLargeReverse',
            action: 'disabled',
          },
          [UserStatus.trialEnded]: {
            cta: t('plan.cta.free.trialEnded'),
            btnStyle: 'gentleLargeReverse',
            action: 'continue',
          },
          [UserStatus.premiumMonthly]: hasCanceled
            ? cacncelledPlanButtonState
            : downgradeButtonState,
          [UserStatus.premiumYearly]: hasCanceled
            ? cacncelledPlanButtonState
            : downgradeButtonState,
          [UserStatus.unlimitedMonthly]: hasCanceled
            ? cacncelledPlanButtonState
            : downgradeButtonState,
          [UserStatus.unlimitedYearly]: hasCanceled
            ? cacncelledPlanButtonState
            : downgradeButtonState,
        },
        premium: {
          [UserStatus.guest]: {
            cta: isUtmExperiment
              ? t('plan.cta.choosePlan', {
                  plan: 'Premium',
                })
              : t('plan.cta.premium.guest'),
            btnStyle: 'louderLarge',
            action: 'login',
          },
          [UserStatus.free]: choosePremiumButtonState,
          [UserStatus.trial]: {
            cta: t('plan.cta.premium.trial'),
            btnStyle: 'louderLarge',
            action: 'upgrade',
          },
          [UserStatus.trialEnded]: choosePremiumButtonState,
          [UserStatus.premiumMonthly]: {
            year: switchToAnnualButtonState,
            month: hasCanceled
              ? resumeSubscriptionButtonState
              : currentPlanButtonState,
          },
          [UserStatus.premiumYearly]: {
            month: switchToMonthlyButtonState,
            year: hasCanceled
              ? resumeSubscriptionButtonState
              : currentPlanButtonState,
          },
          [UserStatus.unlimitedMonthly]: {
            year: downgradeButtonState,
            month: downgradeButtonState,
          },
          [UserStatus.unlimitedYearly]: {
            month: downgradeButtonState,
            year: downgradeButtonState,
          },
        },
        unlimited: {
          [UserStatus.guest]: {
            cta: t('plan.cta.choosePlan', {
              plan: 'Unlimited',
            }),
            btnStyle: 'louderLarge',
            action: 'login',
          },
          [UserStatus.free]: chooseUnlimitedButtonState,
          [UserStatus.trial]: chooseUnlimitedButtonState,
          [UserStatus.trialEnded]: chooseUnlimitedButtonState,
          [UserStatus.premiumMonthly]: {
            year: chooseUnlimitedButtonState,
            month: chooseUnlimitedButtonState,
          },
          [UserStatus.premiumYearly]: {
            month: chooseUnlimitedButtonState,
            year: chooseUnlimitedButtonState,
          },
          [UserStatus.unlimitedMonthly]: {
            year: switchToAnnualButtonState,
            month: hasCanceled
              ? resumeSubscriptionButtonState
              : currentPlanButtonState,
          },
          [UserStatus.unlimitedYearly]: {
            month: switchToMonthlyButtonState,
            year: hasCanceled
              ? resumeSubscriptionButtonState
              : currentPlanButtonState,
          },
        },
      } satisfies PlanButtonState;
    },
    [t]
  );
  return { getButtonState };
}

export const samplePdfUrl =
  'https://media.simplywall.st/site/pro-plan/nasdaqgs-amzn.pdf';
export const samplePdfBrandedUrl =
  'https://media.simplywall.st/site/pro-plan/nasdaqgs-amnz-branded.pdf';
export const sampleCsvUrl =
  'https://media.simplywall.st/site/pro-plan/nasdaqgs-amzn.csv';

export const GrandfatherCoupons = [
  'ynWCzwhz',
  'fkXKDeJw',
  'rXSzIcDS',
  'faIlKV8K',
  'CGghaNKj',
  'JriRYwxp',
];
